/**
 * What `/changes` asks Plone for and how it says it, shared by the page and
 * the `/changes/rss` feed so the two cannot disagree (BL-0129). Pure: no
 * React, no express, so vitest loads it and the server module can import it.
 */
export const CHANGES_PATH = '/changes';
export const FEED_PATH = '/changes/rss';

/** How many documents the page and the feed list. */
export const CHANGES_LIMIT = 100;

/**
 * The newest DOE documents in this archive, by DOE's own issue date. A plain
 * catalog search on the site's `documents` folder: every site has one, and
 * it holds the documents and nothing else.
 */
export const CHANGES_QUERY =
  '/documents/@search?review_state=published&path.depth=1' +
  '&sort_on=issue_date&sort_order=descending' +
  `&b_size=${CHANGES_LIMIT}` +
  '&metadata_fields=issue_date&metadata_fields=docid';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** ISO date -> "Sep 12, 2026". Parsed, not `new Date()`, so the server and
 *  the browser cannot render different days (see FamilyTimeline). */
export const fmtDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[3]}, ${m[1]}` : null;
};

/** The documents that carry an issue date, in the order Plone sorted them. */
export const datedItems = (data) =>
  (data?.items || []).filter((item) => fmtDate(item.issue_date));

/** The document's name as a reader recognises it. */
export const labelOf = (item) =>
  [item.docid, item.title].filter(Boolean).join(' - ') || 'Untitled document';

/** A search result's `@id` as a path on this site's public origin. */
export const docPath = (id, siteId) => {
  let path;
  try {
    path = new URL(id).pathname;
  } catch (e) {
    path = id || '';
  }
  path = path.replace(/^\/\+\+api\+\+/, '');
  const prefix = `/${siteId}`;
  return path === prefix || path.startsWith(`${prefix}/`)
    ? path.slice(prefix.length) || '/'
    : path;
};

/** Documents grouped by issue date, order preserved. */
export const groupByDate = (items) => {
  const groups = [];
  (items || []).forEach((item) => {
    const last = groups[groups.length - 1];
    if (last && last.date === item.issue_date) last.items.push(item);
    else groups.push({ date: item.issue_date, items: [item] });
  });
  return groups;
};

/**
 * `DataFeed` JSON-LD for `/changes` (structured-data task, 2026-09-27): tells
 * an AI assistant or crawler this route is a feed of recently-issued DOE
 * documents, without it having to parse the HTML list. Mirrors
 * `express/changesFeed.js`'s `renderFeed` for the absolute per-document URL
 * (`${origin}${docPath(item['@id'], site.id)}`) so the RSS feed, the on-page
 * list and this JSON-LD can never disagree about what a document's URL is.
 *
 * Null with no site origin or no dated documents, same as the page itself
 * (which then renders "No documents yet." instead of the list).
 */
export const changesDataFeedJsonLd = (site, changes) => {
  const origin = (site?.baseUrl || '').replace(/\/+$/, '');
  const items = datedItems(changes);
  if (!origin || items.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'DataFeed',
    name: `What changed at DOE - ${site.title}`,
    url: `${origin}${CHANGES_PATH}`,
    dataFeedElement: items.map((item) => ({
      '@type': 'DataFeedItem',
      dateModified: item.issue_date,
      item: {
        '@type': 'DigitalDocument',
        name: labelOf(item),
        url: `${origin}${docPath(item['@id'], site.id)}`,
      },
    })),
  };
};
